import type {FC} from 'react';
import {useId} from 'react';

export interface SchematicMapMarker {
  id: string;
  x: number;
  y: number;
  kind: 'point' | 'pin' | 'radius';
  /** Radius in the same coordinate units as `x`/`y`. Only used by `radius` markers. */
  r?: number;
  label?: string;
}

export interface SchematicMapProps {
  markers: SchematicMapMarker[];
  /** SVG preserveAspectRatio. Pass 'none' for decorative empty grids that
   *  should stretch to fill their frame instead of centering. */
  preserveAspectRatio?: string;
}

const POINT_RADIUS = 6;
const PIN_HALF_WIDTH = 8;
const PIN_HEIGHT = 22;
const PADDING_RATIO = 0.25;
const MIN_EXTENT = 20;
const GRID_STEP_DIVISIONS = 6;

const markerExtent = (marker: SchematicMapMarker): number => {
  if (marker.kind === 'radius') return marker.r ?? 0;
  if (marker.kind === 'pin') return PIN_HEIGHT;
  return POINT_RADIUS;
};

interface Bounds {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

const computeBounds = (markers: SchematicMapMarker[]): Bounds => {
  if (markers.length === 0) {
    return {minX: -MIN_EXTENT, minY: -MIN_EXTENT, width: MIN_EXTENT * 2, height: MIN_EXTENT * 2};
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  markers.forEach(marker => {
    const extent = markerExtent(marker);
    minX = Math.min(minX, marker.x - extent);
    maxX = Math.max(maxX, marker.x + extent);
    minY = Math.min(minY, marker.y - extent);
    maxY = Math.max(maxY, marker.y + extent);
  });

  const rawWidth = Math.max(maxX - minX, MIN_EXTENT);
  const rawHeight = Math.max(maxY - minY, MIN_EXTENT);
  const padX = rawWidth * PADDING_RATIO;
  const padY = rawHeight * PADDING_RATIO;

  return {
    minX: minX - padX,
    minY: minY - padY,
    width: rawWidth + padX * 2,
    height: rawHeight + padY * 2,
  };
};

const PinShape: FC<{x: number; y: number}> = ({x, y}) => (
  <path
    d={`M ${x} ${y - PIN_HEIGHT}
        C ${x - PIN_HALF_WIDTH} ${y - PIN_HEIGHT} ${x - PIN_HALF_WIDTH} ${y - PIN_HEIGHT / 2} ${x} ${y}
        C ${x + PIN_HALF_WIDTH} ${y - PIN_HEIGHT / 2} ${x + PIN_HALF_WIDTH} ${y - PIN_HEIGHT} ${x} ${y - PIN_HEIGHT}
        Z`}
    fill="var(--mantine-color-red-6)"
    stroke="var(--mantine-color-body)"
    strokeWidth={1}
  />
);

/**
 * Generic SVG marker plotter over an abstract coordinate space — auto-fits
 * its viewBox around the given markers plus padding.
 * @registry Schematic map: point/pin/radius markers, auto-fit viewBox, theme-aware.
 */
export const SchematicMap: FC<SchematicMapProps> = ({
  markers,
  preserveAspectRatio = 'xMidYMid meet',
}) => {
  const gridId = useId();
  const bounds = computeBounds(markers);
  const gridStep = Math.max(bounds.width, bounds.height) / GRID_STEP_DIVISIONS;

  return (
    <svg
      role="img"
      aria-label="Schematic map"
      viewBox={`${bounds.minX} ${bounds.minY} ${bounds.width} ${bounds.height}`}
      width="100%"
      height="100%"
      style={{display: 'block'}}
      preserveAspectRatio={preserveAspectRatio}>
      <defs>
        <pattern
          id={gridId}
          width={gridStep}
          height={gridStep}
          patternUnits="userSpaceOnUse">
          <path
            d={`M ${gridStep} 0 L 0 0 0 ${gridStep}`}
            fill="none"
            stroke="var(--mantine-color-gray-3)"
            strokeWidth={1}
          />
        </pattern>
      </defs>
      <rect
        x={bounds.minX}
        y={bounds.minY}
        width={bounds.width}
        height={bounds.height}
        fill={`url(#${gridId})`}
      />

      {markers.map(marker => {
        switch (marker.kind) {
          case 'radius':
            return (
              <circle
                key={marker.id}
                cx={marker.x}
                cy={marker.y}
                r={marker.r ?? 0}
                fill="var(--mantine-color-blue-4)"
                fillOpacity={0.2}
                stroke="var(--mantine-color-blue-5)"
                strokeWidth={1}
              />
            );
          case 'pin':
            return <PinShape key={marker.id} x={marker.x} y={marker.y} />;
          case 'point':
          default:
            return (
              <circle
                key={marker.id}
                cx={marker.x}
                cy={marker.y}
                r={POINT_RADIUS}
                fill="var(--mantine-color-blue-6)"
                stroke="var(--mantine-color-body)"
                strokeWidth={2}
              />
            );
        }
      })}

      {markers
        .filter(marker => marker.label)
        .map(marker => (
          <text
            key={`${marker.id}-label`}
            x={marker.x}
            y={
              marker.kind === 'pin'
                ? marker.y - PIN_HEIGHT - 6
                : marker.y - markerExtent(marker) - 6
            }
            textAnchor="middle"
            fontSize={Math.max(bounds.width, bounds.height) / 30}
            fill="var(--mantine-color-text)">
            {marker.label}
          </text>
        ))}
    </svg>
  );
};
